import {
  ArtifactDigestStatus,
  ArtifactTag,
} from '@/services/types/artifact-digest-types';

/**
 * Formats a date string into a human-readable format
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';

  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Returns a color class based on confidence score
 */
export const getConfidenceColor = (confidence: number | null): string => {
  if (confidence === null || confidence === undefined)
    return 'text-muted-foreground';
  if (confidence >= 0.8) return 'text-green-600 dark:text-green-500';
  if (confidence >= 0.5) return 'text-amber-600 dark:text-amber-500';
  return 'text-red-600 dark:text-red-500';
};

/**
 * Returns a color class based on information score
 */
export const getInformationScoreColor = (score: number | null): string => {
  if (score === null || score === undefined) return 'text-muted-foreground';
  if (score >= 0.8) return 'text-blue-600 dark:text-blue-500';
  if (score >= 0.5) return 'text-sky-600 dark:text-sky-500';
  return 'text-slate-600 dark:text-slate-500';
};

/**
 * Returns a badge variant based on digest status
 */
export const getStatusVariant = (
  status: ArtifactDigestStatus | undefined,
): string => {
  if (!status) return 'outline';

  switch (status) {
    case ArtifactDigestStatus.COMPLETED:
      return 'success';
    case ArtifactDigestStatus.IN_PROGRESS:
      return 'default';
    case ArtifactDigestStatus.PENDING:
      return 'secondary';
    case ArtifactDigestStatus.FAILED:
      return 'destructive';
    default:
      return 'outline';
  }
};

/**
 * Returns a badge variant based on tag type
 */
export const getTagVariant = (tag: ArtifactTag): string => {
  switch (tag) {
    case ArtifactTag.MEDICAL_RECORDS:
      return 'emerald';
    case ArtifactTag.POLICE_REPORT_AND_EVIDENCE:
      return 'sky';
    case ArtifactTag.INSURANCE_INFORMATION:
      return 'sky';
    case ArtifactTag.BILLS_AND_LIENS:
      return 'sky';
    case ArtifactTag.CLIENT_CORRESPONDENCE:
      return 'sky';
    case ArtifactTag.UNCLEAR:
    default:
      return 'zinc';
  }
};

/**
 * Truncates text with ellipsis if it exceeds max length
 */
export const truncateText = (text: string, maxLength = 100): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};
